// ============================================================================
// COMPLIANCE USER - PENDING EVIDENCE PAGE
// ============================================================================
// Purpose: View evidence that needs to be submitted for assigned pages
// Mental model: "Show me what evidence I still need to upload."
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import {
  AlertTriangle,
  FileText,
  Upload,
  Clock,
  CheckSquare,
  RefreshCw,
  Search
} from 'lucide-react';
import { complianceExecutionApi, CompliancePage } from '@domains/compliance/execution/api/complianceExecution.api';
import PageInfoButton from '@/components/PageInfoButton';

const PendingEvidence: React.FC = () => {
  const { hasPermission } = useAuth();
  const [pages, setPages] = useState<CompliancePage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    setIsLoading(true);
    try {
      const data = await complianceExecutionApi.getAssignedPages();
      // Filter pages that require evidence and are not submitted
      const pendingPages = data.filter(page =>
        page.evidence_required &&
        page.status !== 'submitted' &&
        page.status !== 'approved_for_audit'
      );
      setPages(pendingPages);
    } catch (error) {
      console.error('Error loading pending evidence pages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'submitted':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-green-700 bg-green-100">
          <CheckSquare size={12} />
          Submitted
        </span>;
      case 'in_progress':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-blue-700 bg-blue-100">
          <Clock size={12} />
          In Progress
        </span>;
      case 'pending':
      case 'not_started':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-yellow-700 bg-yellow-100">
          <AlertTriangle size={12} />
          Pending
        </span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-slate-600 bg-slate-100">
          {status}
        </span>;
    }
  };

  const getStats = () => {
    const total = pages.length;
    const pending = pages.filter(p => p.status === 'pending' || p.status === 'not_started').length;
    const inProgress = pages.filter(p => p.status === 'in_progress').length;

    return { total, pending, inProgress };
  };

  const stats = getStats();
  const filteredPages = pages.filter(page =>
    page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.framework_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!hasPermission('compliance.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 p-8">
          <AlertTriangle className="text-red-500 mb-3" size={36} />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-500 text-sm">You don't have permission to view compliance pages.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading pending evidence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Pending Evidence</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm">Pages requiring evidence submission</p>
          </div>
          <button
            onClick={loadPages}
            className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search pages by title, description, or framework..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-xs font-medium text-slate-500 mb-1">Total Pending</p>
            <p className="text-xl font-bold text-slate-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-xs font-medium text-slate-500 mb-1">Not Started</p>
            <p className="text-xl font-bold text-slate-900">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-xs font-medium text-slate-500 mb-1">In Progress</p>
            <p className="text-xl font-bold text-slate-900">{stats.inProgress}</p>
          </div>
        </div>

        {/* Pages List */}
        <div className="bg-white rounded-3xl border border-slate-100">
          <div className="px-5 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              Pages Requiring Evidence
              <span className="bg-amber-600 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                {filteredPages.length}
              </span>
            </h2>
          </div>
          <div className="p-4">
            {filteredPages.length === 0 ? (
              <div className="py-12">
                <CheckSquare className="text-green-400 mx-auto mb-3" size={40} />
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  {searchTerm ? 'No pages found' : 'All caught up!'}
                </h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto">
                  {searchTerm
                    ? 'Try adjusting your search criteria'
                    : 'You have no pending evidence submissions at this time.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPages.map(page => (
                  <div
                    key={page.id}
                    className="rounded-lg border border-slate-200 p-4 hover:border-amber-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <h3 className="font-semibold text-sm text-slate-900">{page.title}</h3>
                          {getStatusBadge(page.status)}
                        </div>

                        {page.description && (
                          <p className="text-slate-500 text-sm mb-2 line-clamp-2">{page.description}</p>
                        )}

                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-2">
                          {page.framework_name && (
                            <div className="flex items-center gap-1">
                              <FileText size={12} />
                              <span>{page.framework_name}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Section:</span>
                            <span>{page.section}</span>
                          </div>
                          {page.due_date && (
                            <div className="flex items-center gap-1">
                              <Clock size={12} />
                              <span>Due: {new Date(page.due_date).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>

                        {page.evidence_type && (
                          <div className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-50 px-2 py-1 rounded">
                            <FileText size={12} />
                            Evidence Type: {page.evidence_type}
                          </div>
                        )}
                      </div>

                      <Link
                        to={`/compliance/pages/${page.id}`}
                        className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shrink-0"
                      >
                        <Upload size={16} />
                        Upload Evidence
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingEvidence;
